'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

export function WhyOnPrem() {
  const t = useTranslations('whyOnPrem');
  const sectionRef = useRef<HTMLElement>(null);
  // Animations only run while the section is in (or near) the viewport.
  // Off-screen they're a constant 6s tax on compositor + paint — visible as
  // sustained CPU usage in Firefox even after the user has scrolled past.
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setAnimate(true);
      return;
    }
    const io = new IntersectionObserver(entries => {
      for (const e of entries) setAnimate(e.isIntersecting);
    }, { rootMargin: '160px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="cv-auto relative overflow-hidden bg-background-secondary py-24 px-[10%]" data-header-dark>

      {/* Animated semicircle glow rising from bottom-center (ported from
          the old CtaBanner — the belief copy reads like a beacon over it).
          The `whyonprem-glow` / `whyonprem-reflect` classes let globals.css
          kill these animations on Firefox (Gecko rasterises filter:blur
          per-frame and chokes). */}
      <div className="pointer-events-none absolute inset-0">
        {/* Glow beacon — fixed height anchored to bottom so tall mobile sections
            don't stretch the radial-gradient into a uniform wash. */}
        <div className="absolute bottom-0 left-0 right-0 h-[600px]">
          {/* Mobile/tablet: softer pulse */}
          <div className={`whyonprem-glow absolute inset-0 lg:hidden ${animate ? 'animate-cta-pulse-soft' : ''}`} style={{
            background: `radial-gradient(ellipse 100% 120% at 50% 108%,
              rgba(255,255,255,0.86)  0%,
              rgba(147,197,253,0.72)  10%,
              rgba(59,130,246,0.66)  24%,
              rgba(109,40,217,0.56)  42%,
              rgba(67,56,202,0.26)   58%,
              transparent            76%
            )`,
            animationDuration: '6s',
          }} />
          <div className={`whyonprem-glow absolute inset-0 lg:hidden ${animate ? 'animate-cta-pulse-soft' : ''}`} style={{
            background: 'radial-gradient(ellipse 360% 210% at 50% 120%, rgba(109,40,217,0.18) 0%, transparent 68%)',
            animationDuration: '6s',
          }} />

          {/* Desktop: stronger pulse */}
          <div className={`whyonprem-glow absolute inset-0 hidden lg:block ${animate ? 'animate-cta-pulse' : ''}`} style={{
            background: `radial-gradient(ellipse 70% 100% at 50% 105%,
              rgba(255,255,255,0.90)  0%,
              rgba(147,197,253,0.75)  8%,
              rgba(59,130,246,0.70)  20%,
              rgba(109,40,217,0.65)  38%,
              rgba(67,56,202,0.30)   54%,
              transparent            72%
            )`,
            animationDuration: '6s',
          }} />
          <div className={`whyonprem-glow absolute inset-0 hidden lg:block ${animate ? 'animate-cta-pulse' : ''}`} style={{
            background: 'radial-gradient(ellipse 334% 180% at 50% 118%, rgba(109,40,217,0.20) 0%, transparent 65%)',
            animationDuration: '6s',
          }} />
        </div>

        {/* Vertical glass lines — darker base + reflected highlights */}
        <div
          className="absolute inset-0"
          style={{
            maskImage:
              'radial-gradient(ellipse 68% 140% at 50% 122%, transparent 0%, transparent 56%, rgba(0,0,0,0.96) 62%, rgba(0,0,0,0.82) 71%, transparent 84%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 68% 140% at 50% 122%, transparent 0%, transparent 56%, rgba(0,0,0,0.96) 62%, rgba(0,0,0,0.82) 71%, transparent 84%)',
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(8,21,56,0.28) 0px, rgba(8,21,56,0.28) 2px, transparent 2px, transparent 52px)',
              opacity: 0.42,
              mixBlendMode: 'overlay',
              filter: 'blur(0.8px)',
            }}
          />
          <div
            className="whyonprem-reflect absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(220,248,255,0.72) 0px, rgba(220,248,255,0.72) 1px, transparent 1px, transparent 52px)',
              opacity: 0.46,
              mixBlendMode: 'screen',
              animation: animate ? 'cta-reflect-breathe 6s ease-in-out infinite both' : undefined,
              filter: 'blur(1.2px)',
            }}
          />
          <div
            className="whyonprem-reflect absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(241,252,255,0.9) 0px, rgba(241,252,255,0.9) 1px, transparent 1px, transparent 52px)',
              opacity: 0.26,
              mixBlendMode: 'screen',
              animation: animate ? 'cta-reflect-breathe-strong 6s ease-in-out infinite both' : undefined,
              filter: 'blur(1.8px)',
            }}
          />
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto flex flex-col gap-12">

        {/* Eyebrow + headline */}
        <div className="flex flex-col gap-4">
          <p className="font-mono text-eyebrow font-semibold tracking-[var(--hero-label-tracking)] uppercase text-font-quaternary">
            {t('eyebrow')}
          </p>
          <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-primary">
            {t('title')}
          </h2>
        </div>

        {/* Three belief cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 border-t border-white/10 pt-10">
          {([t.raw('card1'), t.raw('card2'), t.raw('card3')] as { title: string; body: string }[]).map((card) => (
            <div key={card.title} className="flex flex-col gap-3">
              <p className="text-[length:var(--text-feature-title)] font-normal text-font-primary leading-snug">{card.title}</p>
              <p className="text-nav text-font-primary/60 font-light leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>

        {/* Founder quote */}
        <figure className="flex flex-col gap-4 border-t border-white/10 pt-10">
          <blockquote className="text-subheadline text-font-primary/80 font-light leading-relaxed max-w-3xl">
            {t('quote')}
          </blockquote>
          <figcaption className="font-mono text-eyebrow uppercase tracking-widest text-font-primary/35">
            {t('attribution')}
          </figcaption>
        </figure>

      </div>
    </section>
  );
}
